import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SERVICES, type RequestDto, type RequestStatus } from '@shared';
import { apiGet, apiPatch, ApiError } from '../../lib/api';
import { StatusChip, StatusBar } from '../../components/StatusChip';
import { MessageThread } from '../../components/MessageThread';
import { useAuth } from '../../lib/auth-context';

// Roles that can advance request status. Mirrors server-side
// requireRbac({ resource: 'engagements', action: 'write' }) — RBAC is the
// authority; this is defence-in-depth + UX affordance.
const ADVANCE_ROLES = new Set(['super', 'admin', 'lead', 'compliance']);

// Next-step transitions per current status. `null` means terminal — no
// forward button is rendered. Linear progression matches the StatusBar
// the operator sees above the actions.
const NEXT_STATUS: Record<RequestStatus, { to: RequestStatus; label: string } | null> = {
  submitted: { to: 'reviewing', label: 'Start triage →' },
  reviewing: { to: 'engagement', label: 'Open engagement →' },
  engagement: { to: 'delivered', label: 'Mark delivered →' },
  delivered: { to: 'closed', label: 'Close engagement →' },
  closed: null,
};

// Operator-side request detail. Mirrors the portal version but with the
// internal-note composer and a link back to the inbox.
export function AdminRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [req, setReq] = useState<RequestDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const role = user?.role ?? '';
  const canAdvance = ADVANCE_ROLES.has(role);

  useEffect(() => {
    if (!id) return;
    apiGet<{ request: RequestDto }>(`/api/requests/${id}`)
      .then((r) => setReq(r.request))
      .catch((err) => setError(err instanceof ApiError ? err.serverMessage : 'Could not load brief.'));
  }, [id]);

  // Auto-clear flash after 4s.
  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 4000);
    return () => window.clearTimeout(t);
  }, [flash]);

  async function advanceStatus(to: RequestStatus): Promise<void> {
    if (!req) return;
    setAdvancing(true);
    setAdvanceError(null);
    try {
      const r = await apiPatch<{ request: RequestDto }>(`/api/requests/${req.id}`, { status: to });
      setReq(r.request);
      setFlash(`Status advanced to ${to}.`);
    } catch (e) {
      setAdvanceError(e instanceof ApiError ? e.serverMessage : 'Could not advance status.');
    } finally {
      setAdvancing(false);
    }
  }

  if (error) {
    return (
      <>
        <h1 className="portal-h1">Brief not found</h1>
        <p className="portal-sub">{error}</p>
        <Link className="btn ghost" to="/admin/inbox" style={{ marginTop: 24 }}>← Inbox</Link>
      </>
    );
  }
  if (!req) return <p className="portal-sub">Loading…</p>;

  const svc = SERVICES.find((s) => s.id === req.service);

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 8 }}>
        <Link to="/admin/inbox" style={{ borderBottom: '1px solid var(--rule)' }}>Inbox</Link>
        {' / '}<span className="rid">{req.id}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">{req.headline}</h1>
          <p className="portal-sub">
            <span className="rid">{req.id}</span> · {svc?.name ?? req.service} · {req.client_name ?? req.client_id} · submitted {req.created_at.slice(0, 10)}
          </p>
        </div>
        <StatusChip status={req.status} />
      </div>

      <div style={{ marginTop: 28 }}>
        <StatusBar status={req.status} />
      </div>

      {/* Status-advance actions. Hidden for terminal `closed` and for roles
          without engagements:write. Server is the authority; non-permitted
          roles see no button so they don't bother trying. */}
      {canAdvance && NEXT_STATUS[req.status] && (
        <div
          style={{
            marginTop: 16,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className="btn accent"
            disabled={advancing}
            onClick={() => advanceStatus(NEXT_STATUS[req.status]!.to)}
          >
            {advancing ? 'Advancing…' : NEXT_STATUS[req.status]!.label}
          </button>
          {advanceError && (
            <span style={{ color: 'var(--red)', fontSize: 13 }}>{advanceError}</span>
          )}
          {flash && !advanceError && (
            <span style={{ color: 'var(--green)', fontSize: 13 }} role="status">{flash}</span>
          )}
        </div>
      )}

      <div className="proposal-form" style={{ alignItems: 'start' }}>
        <div className="card">
          <h3 className="proposal-section-h">Brief</h3>
          <p className="proposal-prose">{req.description}</p>
          {req.counterparty && (
            <>
              <h4 className="proposal-sub-h">Counterparty</h4>
              <p className="proposal-prose">{req.counterparty}</p>
            </>
          )}
        </div>

        <div className="card">
          <h3 className="proposal-section-h">Engagement</h3>
          <div className="proposal-kv"><span>Urgency</span><strong>{req.urgency}</strong></div>
          <div className="proposal-kv"><span>Indicative fee</span><strong>{svc?.fee ?? '—'}</strong></div>
          <div className="proposal-kv"><span>Indicative lead</span><strong>{svc?.lead ?? '—'}</strong></div>
          <div className="proposal-kv"><span>Triage</span><strong>{req.triage}</strong></div>
          <div className="proposal-kv"><span>Engagement value</span><strong>{req.value_pence ? `£${(req.value_pence / 100).toLocaleString()}` : 'TBD'}</strong></div>
          <div className="proposal-kv"><span>Assigned lead</span><strong>{req.assigned_lead_staff_id ?? 'Unassigned'}</strong></div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <h3 className="proposal-section-h">Messages</h3>
        {user ? (
          <MessageThread requestId={req.id} perspective="staff" currentUserId={user.id} />
        ) : (
          <p className="portal-sub">Sign in to view the thread.</p>
        )}
      </div>

      <div className="proposal-actions">
        <Link to="/admin/inbox" className="btn ghost">← Back to inbox</Link>
      </div>
    </>
  );
}
